import { Component } from 'preact';
import {
  GOLD, ACCENT_GOLD, RADIUS, TEXT_PRIMARY, TEXT_MUTED,
  CONTENT_BG_DEFAULT, CornerPlating,
} from '../../lib/medievalTheme';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { getLocalApiOrigin } from '../../utils/localApi';
import { buildTokenImageUrl } from '../../utils/arena/customTokenUrl';
import { getPref, setPref } from '../../utils/persistentPrefs';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { t } from '../../localization/t';

const PANEL_WIDTH_CQW = 20;
// Sized to fit exactly 3 rows of tiles + heading + padding on a
// 1600×900 design canvas: tile width ≈ 90px, tile height ≈ 125px
// (63:88 aspect), 3 rows + 2 gaps ≈ 397px, heading + heading-margin
// ≈ 24px, panel padding 24px → ~445px total ≈ 49.4cqh. 50cqh gives
// a few px of buffer so the third row never clips by rounding.
const PANEL_HEIGHT_CQH = 50;
const LEDGE_WIDTH_CQW = 1.6;
const LEDGE_HEIGHT_CQW = 4.5;
const TILE_ASPECT = 63 / 88;
const GRID_COLUMNS = 3;
// Transparent hit-buffer extending left of the chevron into the
// letterbox. The canvas letterboxes the 16:9 design size inside the
// viewport, so on non-16:9 monitors (most MacBooks are 16:10) the
// left edge of the canvas sits a few pixels inside the screen edge.
// Cursor wandering into that letterbox slice past the chevron was
// triggering mouseLeave and snapping the drawer closed. This buffer
// extends the drawer's hit-area into the slice so the close trigger
// only fires when the cursor is genuinely outside the drawer area.
const HIT_BUFFER_CQW = 5;
const PREF_HOVER_OPEN = 'statusTokenDrawer.hoverOpen';

// Fully opaque dark base (no alpha) so the table never bleeds through
// the panel. The textured CONTENT_BG_DEFAULT layer sits on top of
// this and adds the carved-stone grit; together they read as a
// physical surface rather than a translucent overlay.
const PANEL_BASE = '#0e0a06';

const PANEL_BACKGROUND = `${CONTENT_BG_DEFAULT}, ${PANEL_BASE}`;
const PANEL_BORDER = `1px solid ${GOLD} 0.6)`;
// 3-sided inset rim (top, right, bottom. No left) so the panel's
// left edge has no gold line and reads as flush with the screen
// boundary. The drop shadow and gold halo still extend outward in
// all directions to give the panel its depth, but the rim itself
// stops at the screen edge.
//
// All blur/spread values are in cqw so the panel's depth scales with
// the canvas. 1-2px rim insets stay in px per the project convention
// (CLAUDE.md: "Keep 1–2px borders / shadow blur values in px so they
// render crisp at every scale").
// Inset-only set: rim highlights + inner vignette. Used while the
// drawer is closed so the outset drop shadow + gold halo don't bleed
// into the visible canvas from the panel hidden in the letterbox.
const PANEL_INSET_SHADOW = [
  `inset 0 1px 0 ${GOLD} 0.22)`,
  `inset 0 -1px 0 ${GOLD} 0.22)`,
  `inset -1px 0 0 ${GOLD} 0.22)`,
  'inset 0 0 2.25cqw rgba(0,0,0,0.5)',
].join(', ');

// Open-state shadow: inset rim + the outset drop shadow and gold
// halo that give the panel its floating depth.
const PANEL_LEFT_FLUSH_SHADOW = [
  '0 1.125cqw 3cqw rgba(0,0,0,0.65)',
  `0 0 2.5cqw ${GOLD} 0.12)`,
  PANEL_INSET_SHADOW,
].join(', ');

/**
 * Side-edge ledge that slides out a quick-access token tray on hover.
 * every spawnable token (status markers, minion/creature tokens, and
 * the full-size spawnables), the same set as the B-key Token Browser.
 * Sits
 * fixed at the LEFT edge of the canvas, vertically in the lower-middle
 * band between the logbook (top ~16cqw) and the compendium (bottom-3)
 * so it shares the bottom-left tool cluster without overlapping any
 * other HUD elements.
 *
 * Closed state: only a narrow carved-stone tab pokes past the left
 * edge with the chevron icon.
 * Open state: the entire ledge translates rightward as a unit,
 * revealing the textured panel + grid of token tiles. The chevron
 * stays attached to the panel's right edge, mirroring the deck-tray
 * idiom from BG3-style games.
 *
 * Interactions:
 *   - Hover the ledge → panel slides rightward, transient (closes on
 *     leave after a small grace window).
 *   - Click the chevron → pins the panel open until clicked again.
 *   - mousedown on a tile → starts a drag; ghost follows the cursor;
 *     mouseup outside the drawer fires onSpawn with screen coords.
 *     Drop back inside the drawer cancels cleanly.
 *
 * Props:
 *   tokens. Pile entries with `id`, `cardId`, `name`, `imageUrl`,
 *              `type`, and `isToken` (false = full-size spawnable).
 *              Same shape the B-key Token Browser uses. Empty array
 *              hides the ledge.
 *   onSpawn. (token, { clientX, clientY }) => void.
 *   blocked. When true, hover + click + drag are all suppressed
 *              and the drawer drops below the compendium in the
 *              z-stack so the compendium's expanded panel can cover
 *              the ledge. Used to enforce mutual exclusion with
 *              other one-at-a-time bottom-left tools.
 *   onOpenChange. (open: boolean) => void. Fires whenever the
 *              drawer's effective open state changes. Parent uses
 *              this to gate other one-at-a-time menus.
 */
export default class StatusTokenDrawer extends Component {
  state = {
    hovering: false,
    pinned: false,
    dragging: null,
    cursor: { x: 0, y: 0 },
    // User preference, persisted via persistentPrefs. Default true =
    // drawer opens on hover. When false, only the chevron click or
    // a keyboard interaction opens the drawer; hovering does nothing.
    hoverOpen: getPref(PREF_HOVER_OPEN, true) !== false,
  };

  _hoverCloseTimer = null;
  _wrapperRef = null;

  componentDidMount() {
    document.addEventListener('mousedown', this._onDocMouseDown);
  }

  componentWillUnmount() {
    this._unbindDragListeners();
    document.removeEventListener('mousedown', this._onDocMouseDown);
    removeDismiss(this._escDismissId);
    if (this._hoverCloseTimer) clearTimeout(this._hoverCloseTimer);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.dragging && this.state.dragging) {
      this._bindDragListeners();
    } else if (prevState.dragging && !this.state.dragging) {
      this._unbindDragListeners();
    }
    // When the parent flips `blocked` on (e.g. the compendium opens),
    // force the drawer fully closed so its panel can't sit beneath
    // the compendium and an in-progress drag can't spawn a token
    // behind the new modal context.
    if (!prevProps.blocked && this.props.blocked) {
      this.setState({ hovering: false, pinned: false, dragging: null });
    }
    // Report effective open state to the parent so it can refuse to
    // open siblings while we're up. While open, the drawer owns Escape via
    // the central stack (closes regardless of hover/pin mode).
    const prevOpen = (prevState.hovering || prevState.pinned) && !prevProps.blocked;
    const nowOpen = (this.state.hovering || this.state.pinned) && !this.props.blocked;
    if (prevOpen !== nowOpen) {
      this.props.onOpenChange?.(nowOpen);
      if (nowOpen) this._escDismissId = pushDismiss(() => this.setState({ hovering: false, pinned: false }));
      else removeDismiss(this._escDismissId);
    }
  }

  // Click outside the drawer closes it in click-only mode. Hover mode
  // doesn't need this. MouseLeave handles it. Drag-in-progress is
  // ignored because the user's intent is "drop a token", not "close".
  _onDocMouseDown = (e) => {
    if (this.state.dragging) return;
    if (!this.state.pinned && !this.state.hovering) return;
    if (e.target?.closest?.('[data-status-token-drawer]')) return;
    this.setState({ hovering: false, pinned: false });
  };

  toggleHoverMode = (e) => {
    e?.stopPropagation();
    this.setState((s) => {
      const next = !s.hoverOpen;
      setPref(PREF_HOVER_OPEN, next);
      return { hoverOpen: next };
    });
  };

  _bindDragListeners() {
    window.addEventListener('mousemove', this._onWindowMove);
    window.addEventListener('mouseup', this._onWindowUp);
  }

  _unbindDragListeners() {
    window.removeEventListener('mousemove', this._onWindowMove);
    window.removeEventListener('mouseup', this._onWindowUp);
  }

  _scheduleClose = () => {
    if (this._hoverCloseTimer) clearTimeout(this._hoverCloseTimer);
    this._hoverCloseTimer = setTimeout(() => {
      if (this.state.dragging) return;
      this.setState({ hovering: false });
    }, 140);
  };

  _cancelClose = () => {
    if (this._hoverCloseTimer) {
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
    }
  };

  handleEnter = () => {
    // In click-only mode hover never opens the drawer; the user has
    // to actually click the chevron. We still cancel any pending
    // close timer in case they re-enter while closing.
    this._cancelClose();
    if (this.props.blocked) return;
    if (!this.state.hoverOpen) return;
    this.setState({ hovering: true });
  };

  handleLeave = () => {
    if (!this.state.hoverOpen) return;
    this._scheduleClose();
  };

  handleChevronClick = (e) => {
    e.stopPropagation();
    if (this.props.blocked) return;
    this.setState((s) => ({ pinned: !s.pinned }));
  };

  handleTileDown = (token, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.blocked) return;
    // Same sound the deck-builder sidebar plays on right-click-to-remove
    // (DeckEditor.handleDecrement → playUI(UI.UNEQUIP)). Fires on the
    // mousedown rather than the eventual mouseup so the audio cue is
    // tight to the user's click input. If the drag is cancelled back
    // into the drawer, the sound has already played. That's fine,
    // it represents the click action itself, not the spawn outcome.
    playUI(UI.UNEQUIP);
    this.setState({
      dragging: token,
      cursor: this._canvasCursor(e),
      hovering: false,
    });
  };

  // The drag ghost is `position: fixed` inside `.app-canvas`, which is a
  // containing block for fixed descendants (translateZ(0)). Its left/top are
  // therefore canvas-local, not viewport: subtract the canvas offset from the
  // raw client coords or the ghost drifts away from the cursor by the
  // letterbox offset.
  _canvasCursor = (e) => {
    const cRect = document.querySelector('.app-canvas')?.getBoundingClientRect();
    return cRect
      ? { x: e.clientX - cRect.left, y: e.clientY - cRect.top }
      : { x: e.clientX, y: e.clientY };
  };

  _onWindowMove = (e) => {
    this.setState({ cursor: this._canvasCursor(e) });
  };

  _onWindowUp = (e) => {
    const token = this.state.dragging;
    this.setState({ dragging: null });
    if (!token) return;
    const onDrawer = e.target?.closest?.('[data-status-token-drawer]');
    if (onDrawer) return;
    this.props.onSpawn?.(token, { clientX: e.clientX, clientY: e.clientY });
  };

  renderTile(token) {
    // Pile entries carry a ready `imageUrl`; the cosmetic fallback
    // builds one from a raw catalog row's slug for safety.
    const url = token.imageUrl || buildTokenImageUrl(token, getLocalApiOrigin());
    const label = token.name || token.tokenName || '';
    return (
      <button
        key={token.id || token.cardId || token.tokenSlug}
        type="button"
        title={label}
        aria-label={label ? t('Spawn {name} on the field', { name: label }) : t('Spawn token on the field')}
        // No `transition-transform`. The lift snaps in instantly on
        // hover instead of easing over 150ms, which felt sluggish.
        className="relative hover:scale-105"
        style={{
          width: '100%',
          aspectRatio: TILE_ASPECT,
          padding: '0.25cqw',
          // Recessed slot. Opaque dark base + texture stack so each
          // tile reads as carved into the panel. Inset shadows give
          // the depth; the panel underneath is already opaque so we
          // don't need to fight transparency here.
          background: PANEL_BACKGROUND,
          backgroundBlendMode: 'overlay, normal',
          border: `1px solid ${GOLD} 0.45)`,
          borderRadius: RADIUS.card,
          boxShadow: [
            'inset 0 0.125cqw 0.25cqw rgba(0,0,0,0.55)',
            'inset 0 -1px 0 rgba(255,235,195,0.05)',
            `inset 0 0 0 1px ${GOLD} 0.12)`,
          ].join(', '),
        }}
        // Same sound idiom as the deck-builder sidebar row hover
        // (DeckEditorSidebar `playUI(UI.HOVER, { volume: 0.4 })`)
        // so the two grids feel familiar.
        onMouseEnter={() => playUI(UI.HOVER, { volume: 0.4 })}
        onMouseDown={(e) => this.handleTileDown(token, e)}
        draggable={false}
      >
        <img
          src={url}
          alt=""
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            borderRadius: RADIUS.card,
            boxShadow: '0 0.125cqw 0.375cqw rgba(0,0,0,0.5)',
            pointerEvents: 'none',
          }}
        />
      </button>
    );
  }

  render() {
    const { tokens, blocked, forceOpen } = this.props;
    if (!Array.isArray(tokens) || tokens.length === 0) return null;
    const { hovering, pinned, dragging, cursor } = this.state;
    // When blocked (e.g. compendium is open) we both force the drawer
    // closed and demote it in the stacking context so the compendium
    // panel can cover the ledge cleanly. forceOpen overrides user
    // hover/pin state. Used by the board tutorial to pop the drawer
    // open while the status-tokens step is on screen.
    const isOpen = !blocked && (hovering || pinned || !!forceOpen);

    return (
      <>
        {/* Outer wrapper. Fixed to the left edge in the lower-middle
            band. When closed, the whole element is translated LEFT by
            the panel width so only the chevron ledge pokes past the
            edge. Hovering or click-pinning translates back to 0,
            sliding the panel rightward into view. */}
        <div
          data-status-token-drawer
          onMouseEnter={this.handleEnter}
          onMouseLeave={this.handleLeave}
          style={{
            position: 'fixed',
            left: 0,
            // When closed, only the visible chevron should be a hit
            // target. Not the wrapper's bounding box, which extends
            // across the full panel area (even when translated off-
            // screen) and was triggering opens from the compendium
            // icon below. With `pointer-events: none` on the wrapper
            // and an explicit `pointer-events: auto` on the chevron
            // below, only the chevron receives events; the wrapper's
            // onMouseEnter still fires via bubble from the chevron
            // (CSS spec: "pointer events will trigger event
            // listeners on this parent element... during capture/
            // bubble phases"). Note: `pointer-events` is inherited,
            // so the chevron MUST set its own `auto` explicitly.
            // without that override it would inherit the wrapper's
            // `none` and stop receiving any events.
            pointerEvents: isOpen ? 'auto' : 'none',
            // Bottom-aligned just above the compendium (bottom-3 +
            // 2.5cqw collapsed). 5cqw clears the logbook icon up at
            // top:15.75cqw (collapsed bottom ~18.25cqw) including
            // the panel's gold halo, which extends ~35px past the
            // panel border via the `0 0 40px gold` box-shadow.
            // Anchored at the very bottom of the canvas. Aligned
            // with the compendium's `bottom-3` (0.75cqw) so the
            // drawer's bottom matches the compendium's bottom edge
            // when expanded. Pushed this low so the ledge,
            // vertically centered on the panel, lands comfortably
            // below the logbook icon, while the open panel extends
            // upward into the bottom-left tool zone (overlapping
            // the logbook on open is expected behaviour).
            bottom: '0.75cqw',
            display: 'flex',
            // Center the ledge vertically against the panel. The
            // ledge is a compact tab that reads as a hinge attached
            // to the panel's right side rather than a full-height
            // bar. Anchoring the wrapper low enough means the
            // centered ledge still clears the logbook above.
            alignItems: 'center',
            // The CornerPlating ornaments use inset:-2px so each L-
            // shape extends 2px past the panel's right edge. With a
            // raw translateX(-PANEL_WIDTH_CQW), those 2px of corner
            // plating would still poke past x=0 (the screen-left
            // edge) and be faintly visible. Adding a 0.25cqw buffer
            // to the close-state translation hides them entirely
            // while not affecting the open state.
            transform: `translateX(${isOpen ? '0' : `calc(-${PANEL_WIDTH_CQW}cqw - 0.25cqw)`})`,
            transition: 'transform 220ms cubic-bezier(0.22, 0.9, 0.3, 1)',
            // Above the compendium (z-[1001]), the logbook
            // (z-[999]), and the regular HUD overlays. The
            // drawer's open panel should always paint on top of
            // those tools. Below modals (z-[1100]+) and tooltips
            // (z-[1300]) so dialogs and hover labels still win.
            // When blocked (compendium is open), drop to z-990 so
            // the compendium's z-[1001] panel fully covers the
            // status-token ledge.
            zIndex: blocked ? 990 : 1050,
            // Sized to fit 3x3 tiles without forcing the user to
            // scroll. Stays clear of the logbook icon above (which
            // sits at top: 15.75cqw + 2.5cqw collapsed height) and
            // the compendium below (bottom-3 + 2.5cqw).
            height: `${PANEL_HEIGHT_CQH}cqh`,
          }}
        >
          {/* Letterbox hit-buffer. Invisible extension of the drawer's
              hit-area into the screen-left letterbox slice that exists
              on non-16:9 displays. Active ONLY when the drawer is
              open, so the cursor can wander a few pixels past the
              panel's left edge into off-canvas pixels without
              triggering mouseLeave and snapping the drawer shut.
              When closed the buffer is hit-inactive so cursor passing
              through the letterbox (e.g. moving between monitors)
              can't accidentally trip the drawer open. The right side
              never extends. The panel + chevron bounds are the only
              hit area on the playmat side. */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              right: '100%',
              top: 0,
              bottom: 0,
              width: `${HIT_BUFFER_CQW}cqw`,
              pointerEvents: isOpen ? 'auto' : 'none',
              background: 'transparent',
            }}
          />

          {/* Panel. Leftmost in the flex (which means LEFT of the
              chevron in document flow). When the wrapper translates
              left by panel-width, the panel slides off-screen,
              leaving only the chevron visible at the screen edge. */}
          <div
            style={{
              width: `${PANEL_WIDTH_CQW}cqw`,
              // Strict fixed height that fits exactly 3 rows + the
              // heading + panel padding. With fewer than 9 tokens
              // the grid's `flex: 1; alignContent: start` clusters
              // the real tiles at the top and leaves empty space
              // below. With more than 9 tokens the grid scrolls
              // internally. Either way the panel's external
              // dimensions, the ledge position, and the z-stack
              // are identical across accounts.
              height: `${PANEL_HEIGHT_CQH}cqh`,
              padding: '0.75cqw',
              background: PANEL_BACKGROUND,
              backgroundBlendMode: 'overlay, normal',
              // The panel's right side hands off to the chevron (no
              // border there). The LEFT side sits flush against the
              // screen edge, so it also has no border / no rounding.
              // visually the panel reads as a slab extruding from
              // the side of the screen rather than a free-floating
              // tile with a left edge.
              border: PANEL_BORDER,
              borderLeft: 'none',
              borderRight: 'none',
              borderRadius: 0,
              // Outset shadows only while open; closed uses inset-only
              // so the drop shadow + gold halo don't leak right into
              // the canvas while the panel is hidden in the letterbox.
              boxShadow: isOpen ? PANEL_LEFT_FLUSH_SHADOW : PANEL_INSET_SHADOW,
              transition: 'box-shadow 220ms cubic-bezier(0.22, 0.9, 0.3, 1)',
              isolation: 'isolate',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              // When closed the panel is translated off-canvas to the
              // left (into the letterbox). Disabling pointer-events
              // here keeps that hidden geometry from registering hits
              // Without this, cursor moving through the letterbox
              // would accidentally open the drawer because the panel
              // element is technically there, just invisible.
              pointerEvents: isOpen ? 'auto' : 'none',
            }}
          >
            {/* Only the right-side corner ornaments. The panel's
                left side is flush with the screen edge, so corner
                plating there would just dangle off into the
                letterbox. The panel itself is deliberately square
                (borderRadius: 0 above), so the ornaments carry their
                own curve instead of inheriting the parent's. The square
                panel has no outer arc to trace, so the legacy -2px
                outset is pinned here to keep the ornaments unchanged. */}
            <CornerPlating position="top-right" color={`${GOLD} 0.65)`} radius={8} inset="-2px" />
            <CornerPlating position="bottom-right" color={`${GOLD} 0.65)`} radius={8} inset="-2px" />

            {/* Mode-toggle overlay. Absolutely positioned so it
                doesn't reserve layout space, never pushing the
                heading off-center. Equal top + right inset gives
                a symmetric tuck-in beneath the top-right corner
                ornament. Cursor styling is intentionally omitted;
                the global medieval cursor system handles it. */}
            <button
              type="button"
              onClick={this.toggleHoverMode}
              title={this.state.hoverOpen ? t('Hover-open is on. Click to switch to click-only') : t('Click-only is on. Click to switch to hover-open')}
              aria-label={t('Toggle hover-open behavior')}
              aria-pressed={this.state.hoverOpen}
              style={{
                position: 'absolute',
                top: '0.4cqw',
                right: '0.4cqw',
                width: '1.3cqw',
                height: '1.3cqw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: `1px solid ${GOLD} ${this.state.hoverOpen ? '0.5' : '0.25'})`,
 borderRadius: 'var(--radius-sm)',
 color: this.state.hoverOpen ? ACCENT_GOLD : TEXT_MUTED,
 padding: 0,
 lineHeight: 0,
 transition: 'color 140ms ease, border-color 140ms ease',
 zIndex: 3,
 }}
 onMouseEnter={(e) => { e.currentTarget.style.color = ACCENT_GOLD; }}
 onMouseLeave={(e) => { e.currentTarget.style.color = this.state.hoverOpen ? ACCENT_GOLD : TEXT_MUTED; }}
 >
 {/* Cursor / pointer-mode glyph. The polygon vertices
 are shifted from the previous (5,3) / (10,15) /
 (12,10) / (17,8) so the shape's bounding-box
 center lands exactly on (12,12). The viewBox
 center. Instead of pulling up-left. Fill toggles
 between outline (hover mode) and filled
 (click-only) so the state is visible at a glance. */}
 <svg
 viewBox="0 0 24 24"
 style={{ width: '0.85cqw', height: '0.85cqw', display: 'block' }}
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 strokeLinecap="round"
 strokeLinejoin="round"
 aria-hidden="true"
 >
 <path d="M6 6l5 12 2-5 5-2-12-5z" fill={this.state.hoverOpen ? 'none' : 'currentColor'} />
 </svg>
 </button>

 {/* Heading banner. Small centered label. The mode-toggle
 button is rendered as a panel-level overlay (further
 down) so it never participates in heading layout and
 can't push the title off-center. */}
 <div
 style={{
 marginBottom: '0.45cqw',
 fontSize: '0.6875cqw',
 fontWeight: 700,
 color: TEXT_PRIMARY,
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 textAlign: 'center',
 }}
 className="arena-heading"
 >
 {t('Tokens')}
 </div>

 {/* Tile grid. 3 cols. `overflow-y: scroll` (not auto) so the
                scroll state never flips as content height fluctuates:
                with auto, the loop "scrollbar appears -> grid narrows ->
                tiles shrink -> content fits -> scrollbar disappears ->
                tiles regrow" jittered visibly when 9 tiles sat right on
                the edge of fitting. Native scrollbars are hidden app-wide
                (index.css) and the shared overlay thumb floats above the
                content, so nothing here takes layout width. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
                gap: '0.65cqw',
                flex: 1,
                minHeight: 0,
                // `scroll` (not `auto`) keeps the scrollbar present
                // at all times. scrollbar-gutter handles non-webkit
                // browsers (Firefox) where the custom-track CSS
                // below has no effect.
                overflowY: 'scroll',
                // Setting overflow-y to a non-visible value
                // implicitly makes overflow-x `auto`. That means
                // anything that nudges the layout horizontally.
                // including the hover-scale transform on tiles
                // (Chromium sometimes counts transforms toward
                // scroll overflow even though the spec says it
                // shouldn't). Can pop a horizontal scrollbar.
                // Forcing `hidden` here suppresses the horizontal
                // scrollbar unconditionally; the inner padding
                // below gives hover-scaled tiles enough room to
                // grow without ever reaching the clip edge.
                overflowX: 'hidden',
                alignContent: 'start',
                // Inner padding gives hover-scaled tiles a safe zone
                // to expand into without hitting the scroll
                // container's implicit overflow:hidden on the x-axis
                // (a side-effect of overflow-y:scroll). Each tile
                // can extend up to ~2.5% in any direction on hover;
                // 0.4cqw ≈ 6.4px on the 1600 design canvas is well
                // beyond that, even for the larger tiles in this
                // layout. Negative margin on the grid itself
                // compensates so the visual tile rows still align
                // with the heading above and the panel padding.
                padding: '0.4cqw',
                margin: '-0.4cqw -0.4cqw -0.4cqw -0.4cqw',
              }}
            >
              {tokens.map((t) => this.renderTile(t))}
            </div>

          </div>

          {/* Chevron ledge. Carved-stone tab that's always visible
              past the left edge when the drawer is closed. Click
              toggles the pin; the entire surface is the click target
              so the user can grab the ledge anywhere along its
              vertical run. */}
          <button
            type="button"
            data-tutorial="board-status-tokens-ledge"
            onClick={this.handleChevronClick}
            aria-label={pinned ? t('Close status tokens') : t('Open status tokens')}
            aria-pressed={pinned}
            className="flex items-center justify-center"
            style={{
              width: `${LEDGE_WIDTH_CQW}cqw`,
              // Fixed compact height. The ledge reads as a small
              // hinge tab next to the panel rather than a full-height
              // bar. alignItems:center on the wrapper keeps it
              // vertically centered against the panel.
              height: `${LEDGE_HEIGHT_CQW}cqw`,
              flexShrink: 0,
              background: PANEL_BACKGROUND,
              backgroundBlendMode: 'overlay, normal',
              border: PANEL_BORDER,
              borderLeft: 'none',
              borderRadius: '0 0.4cqw 0.4cqw 0',
              color: ACCENT_GOLD,
              // Three-sided inset rim (top, right, bottom. No left)
              // so the chevron's left edge has no gold line of its
              // own. Combined with the negative marginLeft below, the
              // chevron sits 1px onto the panel and its body covers
              // the panel's right inset rim at the chevron's vertical
              // extent. Net effect: the seam disappears and the
              // chevron reads as a tab cut from the panel rather
              // than a separate piece glued onto it.
              boxShadow: [
                '0 0.5cqw 1.25cqw rgba(0,0,0,0.55)',
                `0 0 1cqw ${GOLD} 0.1)`,
                `inset 0 1px 0 ${GOLD} 0.22)`,
                `inset 0 -1px 0 ${GOLD} 0.22)`,
                `inset -1px 0 0 ${GOLD} 0.22)`,
                'inset 0 0 0.75cqw rgba(0,0,0,0.45)',
              ].join(', '),
              padding: 0,
              isolation: 'isolate',
              position: 'relative',
              marginLeft: '-1px',
              zIndex: 2,
              // pointer-events is inherited. The wrapper sets it to
              // `none` when closed, so the chevron has to override
              // back to `auto` here or it would stop receiving any
              // events (hover, click) and the drawer couldn't open.
              pointerEvents: 'auto',
            }}
          >
            {/* Closed shows '>' (panel comes out rightward when
                opened); open shows '<' (panel will retract). */}
            <svg viewBox="0 0 24 24" style={{ width: '0.9cqw', height: '0.9cqw' }} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points={isOpen ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
            </svg>
          </button>
        </div>

        {/* Drag ghost. Pointer-events:none so window-level mouseup
            hits the canvas underneath for the spawn handoff. */}
        {dragging ? (
          <div
            style={{
              position: 'fixed',
              left: cursor.x,
              top: cursor.y,
              width: '5cqw',
              aspectRatio: TILE_ASPECT,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 2000,
              opacity: 0.94,
            }}
          >
            <img
              src={dragging.imageUrl || buildTokenImageUrl(dragging, getLocalApiOrigin())}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: RADIUS.card,
                border: `2px solid ${ACCENT_GOLD}`,
                boxShadow: '0 0.75cqw 1.75cqw rgba(0,0,0,0.6), 0 0 1.5cqw rgba(232,196,102,0.4)',
              }}
            />
            <div
              className="absolute left-1/2 -bottom-5 -translate-x-1/2 text-[0.55cqw] whitespace-nowrap arena-heading"
              style={{ color: ACCENT_GOLD, textShadow: '0 1px 0.1875cqw rgba(0,0,0,0.9)' }}
            >
              {t('Drop on table')}
            </div>
          </div>
        ) : null}
      </>
    );
  }
}
